<template>
  <div class="chat-fullscreen">
    <div class="sidebar">
      <div class="pa-4">
        <v-btn @click="newChat" outlined block color="primary">
          <v-icon left>mdi-plus</v-icon> Nowy czat
        </v-btn>
      </div>

      <div class="conversations-list">
        <v-list nav dense>
          <v-list-item
            v-for="conv in conversations"
            :key="conv.id"
            :class="{ 'v-list-item--active': selectedConvId === conv.id }"
            @click="selectConversation(conv)"
            class="conv-item"
          >
            <v-list-item-content>
              <v-list-item-title>{{
                conv.title || "Rozmowa"
              }}</v-list-item-title>
            </v-list-item-content>

            <v-list-item-action @click.stop>
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" class="dots-btn">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="openEditDialog(conv)">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="primary" small>mdi-pencil-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Zmień nazwę</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="confirmDelete(conv)">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="error" small>mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="error--text"
                      >Usuń</v-list-item-title
                    >
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
    </div>

    <div class="main-chat">
      <div
        v-if="!selectedConvId"
        class="d-flex flex-column justify-center align-center fill-height"
      >
        <v-icon size="64" color="grey lighten-1">mdi-chat-processing</v-icon>
        <div class="grey--text">Wybierz konwersację, aby rozpocząć</div>
      </div>

      <template v-else>
        <v-toolbar flat dense class="chat-header">
          <v-toolbar-title
            class="subtitle-1 font-weight-bold grey--text text--darken-2"
          >
            {{
              conversations.find((c) => c.id === selectedConvId)?.title ||
              "Rozmowa"
            }}
          </v-toolbar-title>
        </v-toolbar>

        <div class="chat-messages">
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="message.sender === 'user' ? 'user-message' : 'ai-message'"
          >
            <div v-if="message.sender === 'user'">
              {{ message.text }}
            </div>

            <div
              v-else
              v-html="renderMarkdown(message.text)"
              class="markdown-content"
            ></div>
          </div>

          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            class="mt-2"
          ></v-progress-linear>
        </div>

        <div class="input-area">
          <div class="input-container">
            <v-text-field
              v-model="currentMessage"
              placeholder="Zapytaj Czata..."
              outlined
              hide-details
              @keyup.enter="sendMessage"
              class="message-input"
            ></v-text-field>
            <v-btn
              fab
              small
              dark
              color="primary"
              @click="sendMessage"
              :disabled="!currentMessage.trim() || loading"
              class="send-btn"
            >
              <v-icon small>mdi-send</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </div>
    <v-dialog v-model="editDialog" max-width="500px">
      <v-card class="pa-4">
        <div class="overline mb-2" style="font-size: 16px !important">
          Zmień nazwę rozmowy
        </div>
        <v-col cols="12">
          <v-text-field
            v-model="editChatTitle"
            dense
            outlined
            label="Nowa nazwa"
            hide-details
            @keyup.enter="confirmEditChat"
            autofocus
          ></v-text-field>
        </v-col>
        <v-card-actions class="pb-0">
          <v-spacer></v-spacer>
          <v-btn outlined color="grey darken-1" @click="editDialog = false"
            >Anuluj</v-btn
          >
          <v-btn
            color="primary"
            @click="confirmEditChat"
            :disabled="!editChatTitle.trim()"
            >Zapisz</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="newChatDialog" max-width="500px">
      <v-card class="pa-4">
        <div class="overline mb-2" style="font-size: 16px !important">
          Nowa konwersacja
        </div>
        <v-col cols="12">
          <v-text-field
            v-model="newChatTitle"
            dense
            outlined
            label="Tytuł rozmowy"
            placeholder="Np. Serwery Dell R350"
            hide-details
            @keyup.enter="confirmNewChat"
            autofocus
          ></v-text-field>
        </v-col>
        <v-card-actions class="pb-0">
          <v-spacer></v-spacer>
          <v-btn outlined color="grey darken-1" @click="newChatDialog = false"
            >Anuluj</v-btn
          >
          <v-btn
            color="primary"
            @click="confirmNewChat"
            :disabled="!newChatTitle.trim()"
            >Utwórz</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editDialog" max-width="500px">
      <v-card class="pa-4">
        <div class="overline mb-2" style="font-size: 16px !important">
          Zmień nazwę rozmowy
        </div>
        <v-col cols="12">
          <v-text-field
            v-model="editChatTitle"
            dense
            outlined
            label="Nowa nazwa"
            hide-details
            @keyup.enter="confirmEditChat"
            autofocus
          ></v-text-field>
        </v-col>
        <v-card-actions class="pb-0">
          <v-spacer></v-spacer>
          <v-btn outlined color="grey darken-1" @click="editDialog = false"
            >Anuluj</v-btn
          >
          <v-btn
            color="primary"
            @click="confirmEditChat"
            :disabled="!editChatTitle.trim()"
            >Zapisz</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card class="pa-4 text-center">
        <v-icon color="error" size="48" class="mb-3"
          >mdi-alert-circle-outline</v-icon
        >
        <div class="overline mb-2" style="font-size: 16px !important">
          Usuwanie rozmowy
        </div>
        <div class="body-1 mb-4">
          Czy na pewno chcesz usunąć rozmowę <br />
          <strong>"{{ chatToDelete?.title }}"</strong>?
        </div>
        <v-card-actions class="justify-center">
          <v-btn outlined @click="deleteDialog = false">Nie, zostaw</v-btn>
          <v-btn color="error" @click="confirmDeleteAction">Tak, usuń</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MarkdownIt from "markdown-it";

const md = new MarkdownIt({
  breaks: true,
  linkify: true,
});

export default {
  name: "Chat",
  data() {
    return {
      messages: [],
      currentMessage: "",
      conversations: [],
      selectedConvId: null,
      loading: false,
      newChatDialog: false,
      newChatTitle: "",
      deleteDialog: false,
      chatToDelete: null,
      editDialog: false,
      editChatTitle: "",
      chatToEdit: null,
    };
  },
  mounted() {
    this.fetchConversations();
  },
  methods: {
    newChat() {
      this.newChatTitle = "";
      this.newChatDialog = true;
    },
    confirmNewChat() {
      if (!this.newChatTitle.trim()) return;

      this.$store
        .dispatch("createNewConversation", this.newChatTitle)
        .then((response) => {
          this.conversations.unshift(response.data);
          this.selectConversation(response.data);
          this.newChatDialog = false;
        })
        .catch((err) => {
          console.error("Błąd tworzenia:", err);
          this.newChatDialog = false;
        });
    },
    openEditDialog(conv) {
      this.chatToEdit = { ...conv }; // Kopia obiektu
      this.editChatTitle = conv.title;
      this.editDialog = true;
    },
    confirmEditChat() {
      if (!this.editChatTitle.trim() || !this.chatToEdit) return;

      // Przygotowujemy obiekt do wysłania (zgodnie z ConversationEty na backendzie)
      const updatedData = {
        ...this.chatToEdit,
        title: this.editChatTitle,
      };

      this.$store
        .dispatch("updateConversation", {
          id: this.chatToEdit.id,
          data: updatedData,
        })
        .then((response) => {
          const index = this.conversations.findIndex(
            (c) => c.id === this.chatToEdit.id,
          );
          if (index !== -1) {
            this.$set(this.conversations, index, response.data);
          }

          this.editDialog = false;
          this.$store.dispatch("snackbar/setSnackbar", {
            color: "success",
            text: "Nazwa została zaktualizowana",
          });
        })
        .catch((error) => {
          console.error("Błąd aktualizacji:", error);
          this.$store.dispatch("snackbar/setSnackbar", {
            color: "error",
            text: "Nie udało się zmienić nazwy",
          });
        });
    },
    confirmDelete(conv) {
      this.chatToDelete = conv;
      this.deleteDialog = true;
    },
    confirmDeleteAction() {
      if (!this.chatToDelete) return;

      this.$store
        .dispatch("deleteConversation", this.chatToDelete.id)
        .then(() => {
          this.conversations = this.conversations.filter(
            (c) => c.id !== this.chatToDelete.id,
          );
          if (this.selectedConvId === this.chatToDelete.id) {
            this.selectedConvId = null;
            this.messages = [];
          }
          this.deleteDialog = false;
          this.chatToDelete = null;

          this.$store.dispatch("snackbar/setSnackbar", {
            color: "success",
            text: "Konwersacja usunięta",
          });
        });
    },
    renderMarkdown(text) {
      if (!text) return "";
      return md.render(text);
    },
    fetchConversations() {
      this.$store
        .dispatch("getConversations")
        .then((response) => {
          this.conversations = response.data;
        })
        .catch((err) => console.error("Błąd pobierania list konwersacji", err));
    },

    selectConversation(conv) {
      this.selectedConvId = conv.id;
      this.loading = true;
      this.$store
        .dispatch("getConversationMessages", conv.id)
        .then((response) => {
          this.messages = response.data.map((m) => ({
            sender: m.role === "USER" ? "user" : "ai",
            text: m.content,
          }));
          this.scrollToBottom();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    sendMessage() {
      if (!this.currentMessage.trim() || !this.selectedConvId || this.loading)
        return;

      const userText = this.currentMessage;
      this.messages.push({ sender: "user", text: userText });
      this.currentMessage = "";
      this.loading = true;
      this.scrollToBottom();

      this.$store
        .dispatch("sendAiMessage", {
          id: this.selectedConvId,
          message: userText,
        })
        .then((response) => {
          this.messages.push({
            sender: "ai",
            text: response.data.reply,
          });
          this.scrollToBottom();
        })
        .catch(() => {
          this.$store.dispatch("snackbar/setSnackbar", {
            color: "error",
            text: "Błąd podczas wysyłania wiadomości do Gemini",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$el.querySelector(".chat-messages");
        if (container) container.scrollTop = container.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.chat-fullscreen {
  position: fixed;
  top: 64px;
  width: calc(100vw - 104px);
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: row;
  overflow: hidden;
  background-color: white;
}

.sidebar {
  width: 300px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  background-color: #f9f9f9;
}

.conversations-list {
  flex: 1;
  overflow-y: auto;
}

.conv-item {
  cursor: pointer;
}

.dots-btn {
  opacity: 0;
}
.conv-item:hover .dots-btn {
  opacity: 1;
}

.main-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
  height: 100%; /* Upewnij się, że kontener trzyma wysokość */
  overflow: hidden;
}

/* Stylizacja nagłówka czatu */
.chat-header {
  border-bottom: 1px solid #eee;
  background-color: white;
  flex: 0 0 auto;
  height: 48px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
}

.user-message {
  align-self: flex-end;
  margin-bottom: 15px;
  padding: 10px 15px;
  background-color: #e3f2fd;
  border-radius: 15px 15px 0 15px;
  max-width: 80%;
}

.ai-message {
  align-self: flex-start;
  margin-bottom: 15px;
  padding: 10px 15px;
  background-color: #f1f1f1;
  border-radius: 15px 15px 15px 0;
  max-width: 85%;
  line-height: 1.6;
}

.input-area {
  padding: 15px 20px;
  background-color: white;
  border-top: 1px solid #eee;
}

.input-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.message-input {
  flex-grow: 1;
}

.send-btn {
  flex-shrink: 0;
}

.markdown-content >>> p {
  margin-bottom: 10px;
}
.markdown-content >>> p:last-child {
  margin-bottom: 0;
}
.markdown-content >>> ul,
.markdown-content >>> ol {
  margin-bottom: 10px;
  padding-left: 20px;
}
.markdown-content >>> strong {
  font-weight: bold;
}

.conversations-list::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar,
.sidebar::-webkit-scrollbar {
  width: 6px;
}

.conversations-list::-webkit-scrollbar-track,
.chat-messages::-webkit-scrollbar-track,
.sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.conversations-list::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb,
.sidebar::-webkit-scrollbar-thumb {
  background-color: #bdbdbd;
  border-radius: 10px;
}

.conversations-list::-webkit-scrollbar-thumb:hover,
.chat-messages::-webkit-scrollbar-thumb:hover,
.sidebar::-webkit-scrollbar-thumb:hover {
  background-color: #9e9e9e;
}

.conversations-list,
.chat-messages,
.sidebar {
  scrollbar-width: thin;
  scrollbar-color: #bdbdbd transparent;
}
</style>
